import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { forgotPassword, resetPassword } from "../services/auth.ts";

const ForgotPassword: React.FC = () => {
  const [step, setStep] = useState<"request" | "reset">("request");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleRequestCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");
    setLoading(true);
    try {
      const { data } = await forgotPassword(email);
      setInfo(data.message || "If that email is registered, a reset code has been sent.");
      setStep("reset");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setInfo("");
    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await resetPassword(email, code, newPassword);
      navigate("/login");
    } catch (err: any) {
      setError(err.response?.data?.detail || "Could not reset password");
    } finally {
      setLoading(false);
    }
  };

  const fieldClass =
    "rounded-lg px-4 py-3 text-xl font-body border border-border focus:border-accent focus:outline-none bg-white text-ink";
  const labelClass = "text-xl font-semibold mb-1 text-gray-500 font-body";

  return (
    <div className="flex flex-col items-center px-6 py-16">
      <div className="bg-white rounded-lg border border-border p-8 w-full max-w-md flex flex-col items-center">
        <h2 className="text-5xl font-bold mb-6 text-ink font-display">Forgot Password</h2>

        {step === "request" && (
          <form onSubmit={handleRequestCode} className="w-full flex flex-col gap-4">
            <div className="flex flex-col">
              <label className={labelClass}>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className={fieldClass}
                placeholder="you@email.com"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-primary text-white text-xl font-semibold font-body py-3 px-8 rounded-lg hover:bg-ink transition-colors duration-200 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Sending..." : "Send reset code"}
            </button>
          </form>
        )}

        {step === "reset" && (
          <form onSubmit={handleReset} className="w-full flex flex-col gap-4">
            <div className="flex flex-col">
              <label className={labelClass}>6-digit code</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                required
                className={fieldClass}
                placeholder="123456"
              />
            </div>
            <div className="flex flex-col">
              <label className={labelClass}>New password</label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                className={fieldClass}
                placeholder="New password"
              />
            </div>
            <div className="flex flex-col">
              <label className={labelClass}>Confirm new password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className={fieldClass}
                placeholder="Re-enter new password"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="bg-primary text-white text-xl font-semibold font-body py-3 px-8 rounded-lg hover:bg-ink transition-colors duration-200 mt-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Resetting..." : "Reset password"}
            </button>
            <button
              type="button"
              onClick={() => {
                setStep("request");
                setError("");
                setInfo("");
              }}
              className="text-lg font-semibold font-body text-gray-500 hover:text-ink transition-colors duration-200"
            >
              Didn't get a code? Request again
            </button>
          </form>
        )}

        {info && <div className="mt-4 text-lg font-body text-primary text-center">{info}</div>}
        {error && <div className="mt-4 text-xl text-incorrect font-semibold font-body text-center">{error}</div>}
      </div>
    </div>
  );
};

export default ForgotPassword;
